import { useEffect, useMemo, useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { AlertCircle, AlertTriangle, Copy, Check, Loader2, Plus, QrCode } from 'lucide-react'
import clsx from 'clsx'
import Modal from '../../../components/Modal/Modal'
import { useEnrollmentLinks, useCreateEnrollmentLink } from '../../../hooks/useEnrollmentLinks'
import { useMdmEnrollQr } from '../../../hooks/useMdm'
import { errorMessage } from './mobileUtils'

const STEPS_ANDROID_10 = [
  'Restaure o tablet de fábrica (ou use um tablet novo) e ligue-o.',
  'Na tela de boas-vindas, toque 6 vezes seguidas no mesmo ponto da tela.',
  'O leitor de QR abre: conecte no Wi-Fi quando pedir e aponte a câmera para o QR ao lado.',
  'Confirme as telas de "dispositivo gerenciado" — o agente Automais MDM é baixado e vira administrador do aparelho.',
  'No assistente do agente, ative o serviço de acessibilidade "Automais MDM" (necessário para o controle remoto).',
]

const STEPS_ANDROID_9 = [
  'Restaure o tablet de fábrica e ligue-o.',
  'Na tela de boas-vindas, toque 6 vezes seguidas no mesmo ponto.',
  'Conecte no Wi-Fi quando pedir — o Android 9 abre o leitor de QR em seguida; leia o QR ao lado.',
  'Aguarde a criptografia/download do agente e confirme a configuração do dispositivo gerenciado.',
  'No assistente do agente: ative a acessibilidade "Automais MDM" e, na primeira captura de tela, marque "Não mostrar novamente" antes de "Iniciar agora".',
]

/**
 * Modal de adesão de tablet via QR (provisionamento Device Owner).
 * Usa os Links de Adesão do tenant (os mesmos do Connect.IO) — o link define
 * tenant e grupo onde o tablet entra.
 */
export default function EnrollQrModal({ isOpen, onClose, currentGroupId = null, allowedGroupIds = null }) {
  const { data: links = [], isLoading: linksLoading, error: linksError } = useEnrollmentLinks()
  const createLink = useCreateEnrollmentLink()
  const [selectedLinkId, setSelectedLinkId] = useState(null)
  const [androidVersion, setAndroidVersion] = useState('10')
  const [newLabel, setNewLabel] = useState('')
  const [copied, setCopied] = useState(false)
  const [createError, setCreateError] = useState(null)

  // Só links da raiz ou de grupos da aba Mobile: link de grupo de Hosts/VPN (Connect.IO) deixaria o
  // tablet num grupo invisível aqui (o servidor manda esses para a raiz).
  const usable = (l) => l.isActive && !l.isExpired && (!l.groupId || !allowedGroupIds || allowedGroupIds.has(l.groupId))
  const activeLinks = useMemo(() => links.filter(usable), [links, allowedGroupIds]) // eslint-disable-line react-hooks/exhaustive-deps
  const hiddenLinks = links.filter((l) => l.isActive && !l.isExpired && !usable(l)).length

  // Seleciona automaticamente: link do grupo atual, senão o primeiro ativo.
  useEffect(() => {
    if (!isOpen || selectedLinkId || activeLinks.length === 0) return
    const preferred = activeLinks.find((l) => (l.groupId ?? null) === currentGroupId) || activeLinks[0]
    setSelectedLinkId(preferred.id)
  }, [isOpen, activeLinks, currentGroupId, selectedLinkId])

  const { data: qr, isLoading: qrLoading, error: qrError } = useMdmEnrollQr(isOpen ? selectedLinkId : null)

  const qrValue = useMemo(() => {
    if (!qr?.qrPayload) return null
    return typeof qr.qrPayload === 'string' ? qr.qrPayload : JSON.stringify(qr.qrPayload)
  }, [qr])

  const handleCreateLink = async () => {
    setCreateError(null)
    try {
      const created = await createLink.mutateAsync({
        label: newLabel.trim() || 'Tablets MDM',
        groupId: currentGroupId,
      })
      setNewLabel('')
      if (created?.id) setSelectedLinkId(created.id)
    } catch (e) {
      setCreateError(errorMessage(e, 'Não foi possível criar o link de adesão.'))
    }
  }

  const handleCopy = async () => {
    if (!qr?.downloadUrl) return
    try {
      await navigator.clipboard.writeText(qr.downloadUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      /* clipboard bloqueado — o link continua visível para copiar à mão */
    }
  }

  const steps = androidVersion === '9' ? STEPS_ANDROID_9 : STEPS_ANDROID_10

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Cadastrar tablet (QR de adesão)">
      <div className="space-y-5">
        {/* Link de adesão */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Link de adesão</label>
          {linksLoading ? (
            <div className="flex items-center text-sm text-gray-500"><Loader2 className="w-4 h-4 animate-spin mr-2" />Carregando links...</div>
          ) : linksError ? (
            <p className="text-sm text-red-600">{errorMessage(linksError, 'Erro ao carregar links de adesão.')}</p>
          ) : activeLinks.length > 0 ? (
            <select
              value={selectedLinkId || ''}
              onChange={(e) => setSelectedLinkId(e.target.value || null)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              {activeLinks.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.label || l.shortCode} {l.groupName ? `— ${l.groupName}` : '— Principal'} ({l.shortCode})
                </option>
              ))}
            </select>
          ) : (
            <p className="text-sm text-gray-500">Nenhum link de adesão ativo. Crie um abaixo.</p>
          )}

          <div className="flex gap-2 mt-2">
            <input
              type="text"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="Novo link (ex.: Tablets UBS Centro)"
              className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
            />
            <button
              type="button"
              onClick={handleCreateLink}
              disabled={createLink.isPending}
              className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              {createLink.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Criar link
            </button>
          </div>
          {createError && <p className="text-xs text-red-600 mt-1">{createError}</p>}
          <p className="text-xs text-gray-500 mt-1">
            Tablets cadastrados com este link entram no grupo do link.
            {hiddenLinks > 0 && ` ${hiddenLinks} link(s) de grupos de outras abas (ex.: VPN/Hosts) não aparecem aqui.`}
          </p>
        </div>

        {/* QR + instruções */}
        <div className="grid md:grid-cols-2 gap-5">
          <div className="flex flex-col items-center justify-center border border-gray-200 rounded-lg p-4 min-h-[300px]">
            {!selectedLinkId ? (
              <div className="text-center text-gray-400 text-sm"><QrCode className="w-10 h-10 mx-auto mb-2" />Escolha um link de adesão</div>
            ) : qrLoading ? (
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            ) : qrError ? (
              <div className="text-center text-sm text-red-600"><AlertCircle className="w-6 h-6 mx-auto mb-1" />{errorMessage(qrError, 'Erro ao gerar o QR.')}</div>
            ) : qrValue ? (
              <>
                <div className="bg-white p-3 rounded">
                  <QRCodeSVG value={qrValue} size={260} level="M" includeMargin />
                </div>
                {qr.apkAvailable === false && (
                  <div className="mt-3 flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    O APK do agente ainda não está publicado no servidor — o tablet não conseguirá baixá-lo.
                  </div>
                )}
                {qr.downloadUrl && (
                  <button
                    type="button"
                    onClick={handleCopy}
                    className="mt-3 flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800"
                    title={qr.downloadUrl}
                  >
                    {copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                    Copiar link do APK
                  </button>
                )}
              </>
            ) : null}
          </div>

          <div>
            <div className="flex gap-1 mb-3">
              {[
                { v: '10', label: 'Android 10 ou superior' },
                { v: '9', label: 'Android 9' },
              ].map((o) => (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => setAndroidVersion(o.v)}
                  className={clsx(
                    'px-3 py-1 text-xs rounded-full border',
                    androidVersion === o.v
                      ? 'bg-theme-primary text-white border-theme-primary'
                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                  )}
                >
                  {o.label}
                </button>
              ))}
            </div>
            <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
              {steps.map((s) => <li key={s}>{s}</li>)}
            </ol>
            <p className="text-xs text-gray-500 mt-3">
              Teste sem restaurar: <code className="bg-gray-100 px-1 rounded">adb shell dpm set-device-owner io.automais.mdm/.AdminReceiver</code> e
              informe o código <code className="bg-gray-100 px-1 rounded">{qr?.shortCode || '—'}</code> no app.
            </p>
          </div>
        </div>
      </div>
    </Modal>
  )
}
